// Local-development-only auth path — substitutes for Azure AD/MSAL when AUTH_PROVIDER=keycloak.
// Writes to the same localStorage keys MSAL uses (RHYBUS_ACCESS_TOKEN, USER_SESSION_ID) so the
// rest of the app - and @rhybus/api's fetch wrapper - work unmodified regardless of which
// provider issued the token.
import Keycloak from 'keycloak-js'
import { RHYBUS_ACCESS_TOKEN } from './Configuration'
import { USER_SESSION_ID } from './config/session-constants'
import { generateSessionId } from './global'

export const keycloak = new Keycloak({
  url: process.env.REACT_APP_KEYCLOAK_URL!,
  realm: process.env.REACT_APP_KEYCLOAK_REALM!,
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID!,
})

let initPromise: Promise<boolean> | null = null

// Persisted across page refreshes (same tab) so a reload resumes the existing Keycloak session
// via a silent token check instead of forcing a fresh login-required redirect every time.
const KC_TOKEN_KEY = 'kc_token'
const KC_REFRESH_TOKEN_KEY = 'kc_refresh_token'
const KC_ID_TOKEN_KEY = 'kc_id_token'
const KC_SID_KEY = 'kc_sid'

const persistToken = () => {
  if (keycloak.token) {
    localStorage.setItem(RHYBUS_ACCESS_TOKEN, keycloak.token)
    sessionStorage.setItem(KC_TOKEN_KEY, keycloak.token)
  }
  if (keycloak.refreshToken) sessionStorage.setItem(KC_REFRESH_TOKEN_KEY, keycloak.refreshToken)
  if (keycloak.idToken) sessionStorage.setItem(KC_ID_TOKEN_KEY, keycloak.idToken)

  // Keycloak's own "sid" claim identifies the underlying SSO session at the server - it stays
  // the same across page refreshes and silent token refreshes. Only regenerate our own session
  // id when it's genuinely a new Keycloak session (fresh login), not on every reload.
  const currentSid = (keycloak.tokenParsed as { sid?: string } | undefined)?.sid
  const previousSid = sessionStorage.getItem(KC_SID_KEY)
  if (currentSid && currentSid !== previousSid) {
    localStorage.setItem(USER_SESSION_ID, generateSessionId() ?? '')
    sessionStorage.setItem(KC_SID_KEY, currentSid)
  }
}

const clearPersistedTokens = () => {
  sessionStorage.removeItem(KC_TOKEN_KEY)
  sessionStorage.removeItem(KC_REFRESH_TOKEN_KEY)
  sessionStorage.removeItem(KC_ID_TOKEN_KEY)
}

// Idempotent - keycloak-js redirects to the local Keycloak login page itself when there's no
// active session, then resolves once the browser returns with a token. Safe to call from
// multiple components; they all share the same in-flight/resolved promise.
export const initKeycloak = (): Promise<boolean> => {
  if (!initPromise) {
    const storedToken = sessionStorage.getItem(KC_TOKEN_KEY) ?? undefined
    const storedRefreshToken = sessionStorage.getItem(KC_REFRESH_TOKEN_KEY) ?? undefined
    const storedIdToken = sessionStorage.getItem(KC_ID_TOKEN_KEY) ?? undefined

    initPromise = keycloak
      .init({
        // checkLoginIframe defaults to true, which polls Keycloak's session-status iframe. The
        // app typically runs over HTTPS locally while Keycloak (start-dev) is plain HTTP, so the
        // browser blocks that iframe as mixed content and the check hangs until it times out -
        // disable it.
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        // Resume a previous session from sessionStorage instead of always redirecting - keycloak-js
        // will silently refresh these if the access token has expired but the refresh token hasn't.
        token: storedToken,
        refreshToken: storedRefreshToken,
        idToken: storedIdToken,
      })
      .then((authenticated) => {
        persistToken()
        return authenticated
      })
      .catch((error) => {
        // Stored tokens were invalid/expired beyond refresh - clear them so the next call goes
        // through a normal login-required redirect instead of failing on stale data forever.
        clearPersistedTokens()
        throw error
      })
  }
  return initPromise
}

export const keycloakLogout = () => {
  localStorage.removeItem(USER_SESSION_ID)
  clearPersistedTokens()
  sessionStorage.removeItem(KC_SID_KEY)
  return keycloak.logout({ redirectUri: window.location.origin })
}

export const keycloakIsTokenExpired = async (): Promise<boolean> => {
  await initKeycloak()
  return !keycloak.authenticated || keycloak.isTokenExpired()
}

export const keycloakLogoutIfExpired = async () => {
  if (await keycloakIsTokenExpired()) {
    await keycloakLogout()
  }
}

export const keycloakGenerateSilentTokenIfExpired = async () => {
  await initKeycloak()
  try {
    await keycloak.updateToken(30)
    persistToken()
  } catch (error) {
    console.error('Keycloak token refresh failed', error)
    await keycloakLogout()
  }
}
